import { answered, reportsUnreadyMessage } from '@/lib/reportsRead';
import { useNavigate } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { duplicateDonorLeadLabel } from '@signalvote/shared';
import { useFinanceHealth, useFinanceDeadlines } from '@/hooks/queries/useFinanceDeadlines';
import { useCurrentClosePeriod } from '@/hooks/queries/useCloseHub';
import { useFinanceSettings } from '@/hooks/queries/useFinanceSettings';
import { usePortalUploadModel } from '@/hooks/finance/usePortalUploadModel';
import { useOwedReportWindow } from '@/hooks/finance/useOwedReportWindow';
import { getCampaignTodayString } from '@/lib/calendarUtils';
import { DeadlineStatus } from '@signalvote/shared';
import { PreviewReportButton } from './PreviewReportButton';
import { ConfigurationGapAlerts } from './ConfigurationGapAlerts';
import { PendingExportCard } from '../dashboard/FinanceDashboard';
import { fmtDateShort, fmtDateLong, daysUntil, reportTypeName, dueTimeSuffix } from '../utils';
import { FilingExposureNotes } from '../FilingExposureNotes';
import { useHasQueuedObligation } from './useHasQueuedObligation';
import { useOpenAmendments } from '@/components/finance/shared';
import { readySettings } from '@/lib/financeSettingsRead';

/**
 * Close-the-period header — zone 1 of the four-zone layout (header → ranked
 * queue → collapsed upcoming → ledger + readiness strip).
 *
 * One header card carries what the treasurer needs, each fact stated ONCE:
 *   • a tone pill ({N} DAYS / DUE TODAY / {N} DAYS PAST DUE) + the due date,
 *   • the report name as the H1 (period descriptor stripped — the period is on
 *     the "Open period" line, so the name never restates it),
 *   • "Open period: {period}", which absorbs the old OwedPeriodPill; in the gap window
 *     (period closed, deadline not yet passed) it carries a quiet closed-date
 *     sentence that absorbs the old GapWindowExplainer banner,
 *   • the inline Preview affordance (not for RemindOnly) + the as-of stamp.
 *
 * Deliberately dropped vs. the old masthead: the "CLOSE THE PERIOD" eyebrow (it
 * echoed the active tab), the "Clear the flagged items…" instructional subtext,
 * the portal-upload note, and the caught-up reassurance line (when nothing is
 * due the H1 already says so; a second sentence under it only restated it). The state-filer caveat ("closing here doesn't file
 * with the state") now lives in the Generate/File confirm dialog, not the header.
 *
 * Preserved states, re-skinned into the card: RemindOnly framing, the
 * PendingExport takeover, ConfigurationGapAlerts, and the possible-duplicate
 * donor lead.
 *
 * No new data: every hook here is already mounted elsewhere, so this is a
 * re-presentation, not new functionality.
 */

function urgencyTone(days: number): 'blue' | 'amber' | 'rose' {
  if (days <= 7) return 'rose';
  if (days <= 21) return 'amber';
  return 'blue';
}
// The tone pill's label: {N} DAYS (future), DUE TODAY (0), {N} DAYS PAST DUE.
function pillLabel(days: number): string {
  if (days > 0) return `${days} DAYS`;
  if (days === 0) return 'DUE TODAY';
  return `${Math.abs(days)} DAYS PAST DUE`;
}

export function CloseMasthead() {
  // The H1 names the SAME report the pill/date lead with (the report the
  // treasurer will actually work) — the owed report (reportToActOn) when present,
  // else the next chronological deadline. TanStack dedups the shared query keys.
  const healthRead = useFinanceHealth();
  const health = healthRead.status === 'ready' ? healthRead.data : null;
  const healthLoading = healthRead.status === 'loading' || healthRead.status === 'idle';
  // A failed or offline health read leaves the owed report unknown, so the heading falls back to
  // the chronological deadline; the notice below says so instead of passing it off as the owed one.
  const healthUnanswered = healthRead.status === 'failed' || healthRead.status === 'offline';
  const obligationQueued = useHasQueuedObligation();
  const openAmendment = useOpenAmendments()[0] ?? null;
  const deadlinesRead = useFinanceDeadlines({
    status: DeadlineStatus.Upcoming,
    pageSize: 5,
  });
  const deadlines = answered(deadlinesRead);
  const deadlinesLoading = deadlinesRead.status === 'loading' || deadlinesRead.status === 'idle';
  const nextChrono = health?.nextDeadline ?? deadlines?.items?.[0] ?? null;
  const next = health?.reportToActOn ?? nextChrono;
  const settings = readySettings(useFinanceSettings());
  const rulesetCode = settings?.rulesetCode ?? null;

  const portal = usePortalUploadModel();
  const navigate = useNavigate();

  // The close layout names an unread period; the masthead just shows no flag count.
  const closePeriod = useCurrentClosePeriod();
  const duplicateFlagCount = closePeriod.status === 'ready' ? (closePeriod.data?.openDuplicateFlagCount ?? 0) : 0;

  // PendingExportCard takes over the header's action slot until resolved.
  const pendingExport = health?.pendingExportReport ?? null;

  const remindOnly = next?.preparationMode === 'RemindOnly';
  const remindOnlyPortal = portal.stateDisplayName
    ? `${portal.stateDisplayName} ${portal.portalName}`
    : 'state portal';

  const days = next ? daysUntil(next.dueDate) : 0;
  const tone = next ? urgencyTone(days) : 'blue';
  const tonePill = {
    blue: 'bg-blue-100 text-blue-700',
    amber: 'bg-amber-100 text-amber-700',
    rose: 'bg-rose-100 text-rose-700',
  }[tone];

  // Owed-period window + gap-window state, absorbed from the old OwedPeriodPill
  // and GapWindowExplainer strips into the card's "Open period" line.
  // An unanswered owed read shows no open-period line; the health notice above says why.
  const owedRead = useOwedReportWindow();
  const owed = owedRead.status === 'ready' ? owedRead.data : null;
  const today = getCampaignTodayString();
  const inGapWindow = owed != null && today > owed.end && today <= owed.dueDate;

  // The pill/date row + inline Preview are suppressed by the pending-export
  // takeover (it carries its own deadline framing) and reframed for RemindOnly
  // (filed directly in the portal, no file to preview).
  const showDeadlineLine = !!next && !pendingExport && !remindOnly;

  // Until the queries that resolve `next` have loaded, a null `next` means
  // "not known yet", NOT "nothing to file". Show a skeleton rather than
  // fabricate the empty-state heading mid-fetch (ADR-017).
  const resolving = !next && (healthLoading || deadlinesLoading);

  return (
    <div data-testid="close-masthead-header" className="flex flex-col gap-3.5">
      <section className="rounded-2xl border border-slate-200 bg-white px-6 py-5">
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            {!next && reportsUnreadyMessage('filing deadlines', deadlinesRead) && (
              <p
                data-testid="close-masthead-deadlines-unavailable"
                className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-900 leading-snug"
              >
                {reportsUnreadyMessage('filing deadlines', deadlinesRead)}
              </p>
            )}
            {healthUnanswered && (
              <p
                data-testid="close-masthead-health-unavailable"
                className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-900 leading-snug"
              >
                {healthRead.status === 'offline'
                  ? "You’re offline. Which report you owe will load when you reconnect."
                  : "We couldn’t load which report you owe. Refresh to retry."}
              </p>
            )}
            {next && !pendingExport && !remindOnly && (
              <div data-testid="close-deadline-line" className="flex items-baseline gap-3">
                <span
                  className={cn(
                    'inline-flex items-center rounded-md px-2.5 py-1 text-[13px] font-extrabold tracking-[.04em] font-mono',
                    tonePill,
                  )}
                >
                  {pillLabel(days)}
                </span>
                <span className="text-[15px] font-semibold text-slate-500 font-mono">
                  {fmtDateLong(next.dueDate)}
                  {dueTimeSuffix(next.dueTime)}
                </span>
              </div>
            )}

            {resolving ? (
              <div
                data-testid="close-masthead-loading"
                aria-hidden
                className="h-[26px] w-56 rounded bg-slate-100 animate-pulse"
              />
            ) : (
              <h1
                className={cn(
                  'text-[24px] font-extrabold text-slate-900 tracking-[-.02em] leading-tight',
                  showDeadlineLine && 'mt-2.5',
                )}
              >
                {next
                  ? reportTypeName(next.name)
                  : openAmendment
                    ? `Amending ${openAmendment.name}`
                    : obligationQueued
                    ? 'No regular report due right now'
                    : 'Nothing to file right now'}
              </h1>
            )}

            {owed && (
              <p data-testid="close-covers-line" className="text-[14px] text-slate-500 mt-1">
                Open period: <span className={cn(inGapWindow && 'font-medium text-slate-700')}>{owed.periodLabel}</span>.
                {inGapWindow && (
                  <span data-testid="close-gap-explainer" className="text-slate-400">
                    {' '}
                    This period ended on {fmtDateShort(owed.end)}. Later activity is tracked separately, not part of
                    this filing.
                  </span>
                )}
              </p>
            )}

            {next && !pendingExport && remindOnly && (
              <p data-testid="close-remind-only" className="text-[12.5px] text-slate-600 leading-snug mt-1.5">
                File this one directly in the {remindOnlyPortal}. We don&apos;t prepare a file for it.
              </p>
            )}

            {next && <FilingExposureNotes deadline={next} testIdPrefix="close" />}

            {duplicateFlagCount > 0 && (
              <p data-testid="close-duplicate-flags" className="text-[13px] text-slate-500 mt-1.5">
                {duplicateDonorLeadLabel(duplicateFlagCount)}
              </p>
            )}
          </div>

          <div className="flex shrink-0 flex-col items-end gap-2">
            {next && !pendingExport && !remindOnly && (
              <PreviewReportButton deadlineId={next.id} rulesetCode={rulesetCode} />
            )}
            <span className="text-[11.5px] text-slate-500 font-medium font-mono whitespace-nowrap">
              As of{' '}
              {new Date().toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </span>
          </div>
        </div>
      </section>

      {/* Schedule configuration gaps: alert-don't-block banners explaining WHY part
          of the filing calendar isn't generating. The server ships the copy; this is
          their only frontend consumer. */}
      <ConfigurationGapAlerts gaps={deadlines?.configurationGaps ?? []} />

      {/* PendingExportCard takeover: full-width where the queue would go, until
          the treasurer confirms the upload. Behavior unchanged. */}
      {pendingExport && (
        <PendingExportCard
          pendingExportReport={pendingExport}
          onGoConfirm={(reportId) =>
            void navigate({ to: '/admin/finance/reports/$reportId', params: { reportId } })
          }
        />
      )}
    </div>
  );
}
